import { useCallback, useEffect, useRef } from "react";
import { useBlocker } from "react-router-dom";
import { getIsLoggingOut } from "../utils/auth-storage";

export interface NavigationGuardOptions {
  /**
   * Callback triggered when navigation is attempted and blocked
   */
  onNavigationAttempt: () => void;
  /**
   * bypass guard with condition
   */
  hasByPassGuard?: boolean;
}

export const useNavigationGuard = ({
  onNavigationAttempt,
  hasByPassGuard = false,
}: NavigationGuardOptions) => {
  const bypassNextNavigation = useRef(hasByPassGuard);

  // Block navigation when guard is active and navigating to a different route
  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    // Logout owns the session teardown and runs its own flush; never trap its
    // navigation behind an unsaved-changes prompt (e.g. the idle timeout).
    if (getIsLoggingOut()) return false;

    if (bypassNextNavigation.current) {
      bypassNextNavigation.current = false;

      return false;
    }
    return currentLocation.pathname !== nextLocation.pathname;
  });

  // Trigger callback when navigation is blocked
  useEffect(() => {
    if (blocker.state === "blocked") {
      onNavigationAttempt();
    }
  }, [blocker.state, onNavigationAttempt]);

  /**
   * Proceed with the blocked navigation
   */
  const proceed = useCallback(() => {
    if (blocker.state === "blocked") {
      blocker.proceed();
    }
  }, [blocker]);

  /**
   * Cancel the blocked navigation and stay on current page
   */
  const reset = useCallback(() => {
    if (blocker.state === "blocked") {
      blocker.reset();
    }
  }, [blocker]);

  /**
   * Execute navigation without triggering the guard
   * Use this for programmatic navigation (Save & Close, etc.)
   */
  const navigateWithoutGuard = useCallback((navigationFn: () => void) => {
    bypassNextNavigation.current = true;
    navigationFn();
  }, []);

  return {
    proceed,
    reset,
    navigateWithoutGuard,
    isBlocked: blocker.state === "blocked",
  };
};
